import { createAsyncThunk } from "@reduxjs/toolkit";

//Include Both Helper File with needed methods
import {
    getUser as getUserApi,
    getBusinessSites as getBusinessSitesApi,
    getBusinessDepartments as getBusinessDepartmentsApi,
    updateUserProfile as updateUserProfileApi,
    uploadUserAvatar as uploadUserAvatarApi,
    removeUserAvatar as removeUserAvatarApi,
} from "../../helpers/backend_helper";

import { setCurrentStepAction, setLoaderAction } from './reducer';

// Import realtime services for business switching
import notificationAggregator from '../../services/realtimeNotifications';
import dashboardManager from '../../services/realtimeDashboard';
import supportTicketManager from '../../services/realtimeSupportTickets';
import licenseManager from '../../services/realtimeLicenses';
import notificationStore from '../../services/notificationStore';

export const setLoader = () => async (dispatch: any) => {
    try {
        dispatch(setLoaderAction());
    } catch (error) {
        // console.log(error);
    }
};

export const setCurrentStep = (data: any) => async (dispatch: any) => {
    try {
        dispatch(setCurrentStepAction(data));
    } catch (error) {
        // console.log(error);
    }
};

export const getUser = createAsyncThunk("co2profile/getUser", async () => {
    try {
        const response = getUserApi();
        return response;
    } catch (error) {
        return error;
    }
});

export const getBusinessSites = createAsyncThunk("co2profile/getBusinessSites", async () => {
    try {
        const response = getBusinessSitesApi();
        return response;
    } catch (error) {
        return error;
    }
});

export const getBusinessDepartments = createAsyncThunk("co2profile/getBusinessDepartments", async () => {
    try {
        const response = getBusinessDepartmentsApi();
        return response;
    } catch (error) {
        return error;
    }
});

export const updateUserProfile = createAsyncThunk("co2profile/updateUserProfile", async (data: any) => {
    try {
        const response = updateUserProfileApi(data);
        return response;
    } catch (error) {
        return error;
    }
});

export const uploadUserAvatar = createAsyncThunk("co2profile/uploadUserAvatar", async (data: any) => {
    try {
        const response = uploadUserAvatarApi(data);
        return response;
    } catch (error) {
        return error;
    }
});

export const removeUserAvatar = createAsyncThunk("co2profile/removeUserAvatar", async (data: any) => {
    try {
        const response = removeUserAvatarApi(data);
        return response;
    } catch (error) {
        return error;
    }
});

export const updateUserEmail = createAsyncThunk("co2profile/updateUserEmail", async (data: any) => {
    try {
        const response = updateUserProfileApi(data);
        return response;
    } catch (error) {
        return error;
    }
});

export const updateUserMobile = createAsyncThunk("co2profile/updateUserMobile", async (data: any) => {
    try {
        const response = updateUserProfileApi(data);
        return response;
    } catch (error) {
        return error;
    }
});

/**
 * Handle business switching - Clean up old business channels and reinitialize with new business
 * This should be called whenever the user switches to a different business context
 * 
 * @param oldBusinessId - The previous business ID (to clean up channels)
 * @param newBusinessId - The new business ID (to subscribe to new channels)
 * @param userId - The current user ID
 */
export const handleBusinessSwitch = async (
    oldBusinessId: number | null,
    newBusinessId: number,
    userId: number
): Promise<void> => {
    console.log('[BusinessSwitch] Switching from business', oldBusinessId, 'to', newBusinessId);

    try {
        // Step 1: Disconnect all existing realtime subscriptions
        console.log('[BusinessSwitch] Disconnecting notification aggregator...');
        notificationAggregator.disconnect();

        // Step 2: Disconnect dashboard manager (it manages its own channels)
        console.log('[BusinessSwitch] Disconnecting dashboard manager...');
        dashboardManager.disconnectAll();

        // Step 3: Clear business-specific notifications from IndexedDB
        if (oldBusinessId) {
            console.log('[BusinessSwitch] Clearing notifications for old business', oldBusinessId);
            await notificationStore.clearBusinessNotifications(oldBusinessId);
        }

        // Step 4: Re-initialize notification aggregator with new business context
        console.log('[BusinessSwitch] Reinitializing notification aggregator for new business', newBusinessId);
        await notificationAggregator.initialize(userId, newBusinessId);

        console.log('[BusinessSwitch] Business switch completed successfully');
    } catch (error) {
        console.error('[BusinessSwitch] Error during business switch:', error);
        throw error;
    }
};

/**
 * Thunk action to handle business switching with Redux integration
 * Call this from components when business context changes
 */
export const switchBusiness = createAsyncThunk(
    "co2profile/switchBusiness",
    async ({ oldBusinessId, newBusinessId, userId }: {
        oldBusinessId: number | null;
        newBusinessId: number;
        userId: number;
    }) => {
        try {
            await handleBusinessSwitch(oldBusinessId, newBusinessId, userId);
            return { success: true };
        } catch (error) {
            console.error('[switchBusiness] Failed to switch business:', error);
            throw error;
        }
    }
);